export interface Translation {
    nav: {
        home: string;
        features: string;
        pricing: string;
        about: string;
        contact: string;
        download: string;
        login: string;
        getStarted: string;
    };
    hero: {
        title: string;
        subtitle: string;
        description: string;
        cta: {
            primary: string;
            secondary: string;
        };
        stats: {
            users: string;
            languages: string;
            countries: string;
        };
    };
    features: {
        title: string;
        subtitle: string;
        items: Array<{
            icon: string;
            title: string;
            description: string;
        }>;
    };
    howItWorks: {
        title: string;
        subtitle: string;
        steps: Array<{
            number: string;
            title: string;
            description: string;
        }>;
    };
    pricing: {
        title: string;
        subtitle: string;
        monthly: string;
        yearly: string;
        plans: Array<{
            name: string;
            price: string;
            period: string;
            description: string;
            features: string[];
            cta: string;
            popular?: boolean;
        }>;
    };
    cta: {
        title: string;
        subtitle: string;
        button: string;
        note: string;
    };
    footer: {
        company: {
            title: string;
            links: string[];
        };
        product: {
            title: string;
            links: string[];
        };
        resources: {
            title: string;
            links: string[];
        };
        legal: {
            title: string;
            links: string[];
        };
        copyright: string;
        madeWith: string;
    };
}

export const translations: Record<string, Translation> = {
    en: {
        nav: {
            home: 'Home',
            features: 'Features',
            pricing: 'Pricing',
            about: 'About',
            contact: 'Contact',
            download: 'Download',
            login: 'Login',
            getStarted: 'Get Started',
        },
        hero: {
            title: 'Real-Time Auto-Translation Video Calls/Conference',
            subtitle: 'The best Gateway to Worldwide Collaboration!',
            description: 'Video calls, real-time auto-translation, and AI-powered voice recognition for crystal-clear communication! Experience wonderful moments on macOS and Windows desktops.',
            cta: {
                primary: 'Download for Free',
                secondary: 'Watch Demo',
            },
            stats: {
                users: '50K+ Active Users',
                languages: '30+ Languages',
                countries: '30+ Countries',
            },
        },
        features: {
            title: 'Built for Professional Communication',
            subtitle: 'Everything you need for productive desktop collaboration',
            items: [
                {
                    icon: '💬',
                    title: 'Real-time Translation Chat',
                    description: 'Instant messaging with group chats, reactions, and file sharing',
                },
                {
                    icon: '📹',
                    title: 'High-Quality Video Calls',
                    description: 'Video calls and video conferences with screen sharing support',
                },
                {
                    icon: '🎙️',
                    title: 'Voice Recognition to Text',
                    description: 'AI-powered real-time voice recognition during video calls and conferences',
                },
                {
                    icon: '🌍',
                    title: 'Multi-language Real-time Translation',
                    description: 'Customized translation for global languages worldwide',
                },
                {
                    icon: '🔒',
                    title: 'Secure Authentication',
                    description: 'Supabase secure authentication and End-to-End encrypted chat',
                },
                {
                    icon: '💻',
                    title: 'Dedicated Desktop App',
                    description: 'Native app optimized for macOS and Windows',
                },
            ],
        },
        howItWorks: {
            title: 'How It Works',
            subtitle: 'Get started in 3 simple steps',
            steps: [
                {
                    number: '1',
                    title: 'Sign Up',
                    description: 'Create your free account in seconds',
                },
                {
                    number: '2',
                    title: 'Invite Team',
                    description: 'Add team members from around the world',
                },
                {
                    number: '3',
                    title: 'Start Collaborating',
                    description: 'Chat, call, and work together seamlessly',
                },
            ],
        },
        pricing: {
            title: 'Simple, Transparent Pricing',
            subtitle: 'Choose the plan that fits your needs',
            monthly: 'Monthly',
            yearly: 'Yearly',
            plans: [
                {
                    name: 'Free',
                    price: '$0 USD',
                    period: '/month',
                    description: 'Perfect for trying out',
                    features: [
                        '✅ 1:1 real-time auto translation chat (50 messages/day, no group chat)',
                        '✅ 1:1 real-time auto translation video calls (20 min/day for caller, unlimited for receiver, no group calls)',
                        '✅ File sharing in chat (50MB/day)',
                        '✅ AI voice recognition (STT) in chat & calls (500 characters/day, real-time auto translation free)',
                    ],
                    cta: 'Subscribe',
                },
                {
                    name: 'Personal',
                    price: '$20.00 USD',
                    period: '/month',
                    description: 'For individual professionals',
                    features: [
                        '✅ 1:1 real-time auto translation chat (500 messages/day, no group chat)',
                        '✅ 1:1 real-time auto translation video calls (60 min/day for caller, unlimited for receiver, no group calls)',
                        '✅ File sharing in chat (100MB/file, 250MB/day)',
                        '✅ AI voice recognition (STT) in chat & calls (5,000 characters/day, real-time auto translation free)',
                    ],
                    cta: 'Subscribe',
                },
                {
                    name: 'Business',
                    price: '$40.00 USD',
                    period: '/month',
                    description: 'For businesses',
                    features: [
                        '✅ Real-time auto translation group chat (unlimited)',
                        '✅ Real-time auto translation group video calls (unlimited, max 4 per group)',
                        '✅ File sharing in chat (100MB/file, 500MB/day)',
                        '✅ Individual download for chat messages and attachments',
                        '✅ AI voice recognition (STT) in chat & calls (10,000 characters/day, real-time auto translation free)',
                    ],
                    cta: 'Subscribe',
                    popular: false,
                },
                {
                    name: 'Enterprise',
                    price: '$60.00 USD',
                    period: '/month',
                    description: 'For unlimited use',
                    features: [
                        '✅ Real-time auto translation group chat (unlimited)',
                        '✅ Real-time auto translation group video calls (unlimited, max 4 per group)',
                        '✅ Real-time auto translation video conferencing (unlimited, max 12 per conference)',
                        '✅ Video call and conference recording',
                        '✅ Screen sharing in video calls and conferences',
                        '✅ File sharing in chat (100MB/file, 1GB/day)',
                        '✅ Bulk download for chat messages and attachments',
                        '✅ Unlimited AI voice recognition (STT) in chat/calls/conferences (real-time auto translation free)',
                    ],
                    cta: 'Subscribe',
                },
            ],
        },
        cta: {
            title: 'Ready to Transform Your Global Communication?',
            subtitle: 'Join thousands of teams already using AivueBiz',
            button: 'Start Your Free Trial',
            note: 'No credit card required • Start free today',
        },
        footer: {
            company: {
                title: 'Company',
                links: ['About Us', 'Careers', 'Press', 'Blog'],
            },
            product: {
                title: 'Product',
                links: ['Features', 'Pricing', 'Security', 'Download'],
            },
            resources: {
                title: 'Resources',
                links: ['Documentation', 'API', 'Support', 'Status'],
            },
            legal: {
                title: 'Legal',
                links: ['Privacy Policy', 'Terms of Service', 'Refund Policy', 'Cookie Policy'],
            },
            copyright: '© 2025~ Groove Inc. All rights reserved.',
            madeWith: 'Made in South Korea',
        },
    },
    ko: {
        nav: {
            home: '홈',
            features: '기능',
            pricing: '요금제',
            about: '소개',
            contact: '문의',
            download: '다운로드',
            login: '로그인',
            getStarted: '시작하기',
        },
        hero: {
            title: '글로벌 비즈니스 파트너를 위한 실시간 자동번역 영상통화/회의',
            subtitle: '전 세계와 소통하는 최고의 게이트웨이!',
            description: '영상 통화, 실시간 자동번역, AI 기반 음성인식으로 선명한 커뮤니케이션! macOS와 Windows 데스크탑에서 멋진 순간을 경험하세요.',
            cta: {
                primary: '무료 다운로드',
                secondary: '데모 보기',
            },
            stats: {
                users: '50K+ 활성 사용자',
                languages: '30개 이상 언어',
                countries: '30개국 이상',
            },
        },
        features: {
            title: '전문적인 커뮤니케이션을 위한 설계',
            subtitle: '생산적인 데스크톱 협업에 필요한 모든 기능',
            items: [
                {
                    icon: '💬',
                    title: '실시간 번역 채팅',
                    description: '그룹 채팅, 리액션, 파일 공유 기능이 포함된 인스턴트 메시징',
                },
                {
                    icon: '📹',
                    title: '고화질 영상 통화',
                    description: '영상통화 및 영상 컨퍼런스, 화면 공유 지원',
                },
                {
                    icon: '🎙️',
                    title: '음성인식-텍스트 변환',
                    description: '영상 통화 및 영상 컨퍼런스 중 AI 기반 실시간 음성인식',
                },
                {
                    icon: '🌍',
                    title: '다국어 실시간 번역',
                    description: '전세계 글로벌 언어로 맞춤 번역',
                },
                {
                    icon: '🔒',
                    title: '안전한 인증',
                    description: 'Supabase 안전 인증 및 End-to-End 암호화 채팅',
                },
                {
                    icon: '💻',
                    title: '데스크톱용 전용 앱',
                    description: 'macOS와 Windows에 최적화된 네이티브 앱',
                },
            ],
        },
        howItWorks: {
            title: '이용 방법',
            subtitle: '간단한 3단계로 시작하세요',
            steps: [
                {
                    number: '1',
                    title: '회원가입',
                    description: '몇 초 만에 무료 계정 생성',
                },
                {
                    number: '2',
                    title: '팀 초대',
                    description: '전 세계 팀원들을 추가',
                },
                {
                    number: '3',
                    title: '협업 시작',
                    description: '채팅, 통화, 원활한 협업',
                },
            ],
        },
        pricing: {
            title: '간단하고 투명한 요금제',
            subtitle: '필요에 맞는 요금제를 선택하세요',
            monthly: '월간',
            yearly: '연간',
            plans: [
                {
                    name: '무료',
                    price: '$0 USD',
                    period: '/월',
                    description: '체험용으로 적합',
                    features: [
                        '✅ 실시간 자동 번역 1:1 채팅 (일 50회 메시지 전송, 그룹채팅 안됨)',
                        '✅ 실시간 자동 번역 1:1 영상 통화 (발신자에게만 일 20분 적용, 수신자는 무제한, 그룹영상통화 안됨)',
                        '✅ 채팅에서 파일 공유 (50MB/일)',
                        '✅ 채팅/영상통화에서 AI 음성인식(500자/일, 실시간 자동번역 무료)',
                    ],
                    cta: '구독하기',
                },
                {
                    name: '개인',
                    price: '$20.00 USD',
                    period: '/월',
                    description: '개인 전문가용',
                    features: [
                        '✅ 실시간 자동 번역 1:1 채팅 (일 500회 메시지 전송, 그룹채팅 안됨)',
                        '✅ 실시간 자동 번역 1:1 영상 통화 (발신자에게만 일 60분 적용, 수신자는 무제한, 그룹영상통화 안됨)',
                        '✅ 채팅에서 파일 공유 (100MB/파일, 250MB/일)',
                        '✅ 채팅/영상통화에서 AI 음성인식(5,000자/일, 실시간 자동번역 무료)',
                    ],
                    cta: '구독하기',
                },
                {
                    name: '비즈니스',
                    price: '$40.00 USD',
                    period: '/월',
                    description: '비즈니스용',
                    features: [
                        '✅ 실시간 자동 번역 그룹 채팅(무제한)',
                        '✅ 실시간 자동 번역 그룹 영상 통화(무제한, 그룹당 최대 4명)',
                        '✅ 채팅에서 파일 공유 (100MB/파일, 500MB/일)',
                        '✅ 채팅메시지 및 첨부파일 개별 다운로드 기능',
                        '✅ 채팅/영상통화에서 AI 음성인식(10,000자/일, 실시간 자동번역 무료)',
                    ],
                    cta: '구독하기',
                    popular: false,
                },
                {
                    name: '엔터프라이즈',
                    price: '$60.00 USD',
                    period: '/월',
                    description: '무제한용',
                    features: [
                        '✅ 실시간 자동 번역 그룹 채팅(무제한)',
                        '✅ 실시간 자동 번역 그룹 영상 통화(무제한, 그룹당 최대 4명)',
                        '✅ 실시간 자동 번역 그룹 화상 회의(무제한, 회의당 최대 12명)',
                        '✅ 영상 통화 및 화상 회의 녹화 기능',
                        '✅ 영상 통화와 영상 회의에서 화면 공유 기능',
                        '✅ 채팅에서 파일 공유 (100MB/파일, 1GB/일)',
                        '✅ 채팅메시지 및 첨부파일 일괄 다운로드 기능',
                        '✅ 채팅/영상통화/영상회의에서 AI 음성인식 무제한(실시간 자동번역 무료)',
                    ],
                    cta: '구독하기',
                },
            ],
        },
        cta: {
            title: '글로벌 커뮤니케이션을 혁신할 준비가 되셨나요?',
            subtitle: '이미 수천 개의 팀이 AivueBiz를 사용하고 있습니다',
            button: '무료 체험 시작하기',
            note: '신용카드 불필요 • 무료로 시작 가능',
        },
        footer: {
            company: {
                title: '회사',
                links: ['회사 소개', '채용', '보도자료', '블로그'],
            },
            product: {
                title: '제품',
                links: ['기능', '요금제', '보안', '다운로드'],
            },
            resources: {
                title: '리소스',
                links: ['문서', 'API', '지원', '상태'],
            },
            legal: {
                title: '법적 고지',
                links: ['개인정보처리방침', '이용약관', '환불 정책', '쿠키 정책'],
            },
            copyright: '© 2025~ Groove Inc. All rights reserved.',
            madeWith: 'Made in South Korea',
        },
    },
};

export function detectLanguage(): string {
    const browserLang = navigator.language || 'en';
    if (browserLang.toLowerCase().includes('ko')) {
        return 'ko';
    }

    // Check URL params
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam === 'ko') {
        return 'ko';
    }

    return 'en';
}

export function getTranslation(lang: string): Translation {
    return translations[lang] || translations.en;
}
